// Offline upload queue for web customer-folder photos/scans.
// Files are stored as Blobs in IndexedDB while offline and auto-uploaded when the
// connection returns (or on next app load). localStorage can't hold binary, hence IDB.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const DB_NAME = "amscale-offline";
const STORE = "uploads";

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "id" });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function putItem(item) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, "readwrite");
    t.objectStore(STORE).put(item);
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}

export async function allItems() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, "readonly").objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

async function delItem(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, "readwrite");
    t.objectStore(STORE).delete(id);
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}

const subs = new Set();
const notify = () => subs.forEach((fn) => fn());
export const subscribe = (fn) => { subs.add(fn); return () => subs.delete(fn); };
export const pendingCount = async () => (await allItems()).length;

export async function enqueue({ base, customerKey, customerName, invoice_number, filename, blob }) {
  const id = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random()}`;
  await putItem({ id, base, customerKey, customerName, invoice_number: invoice_number || "", filename, blob, createdAt: Date.now() });
  notify();
}

let flushing = false;
// Try to upload everything queued. Stops on the first failure while offline so we
// don't spin; a 4xx (bad request) item is dropped so it can't block the queue forever.
export async function flush() {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  try {
    const items = await allItems();
    for (const it of items) {
      try {
        const form = new FormData();
        form.append("file", it.blob, it.filename);
        form.append("invoice_number", it.invoice_number || "");
        await api.post(it.base, form);
        await delItem(it.id);
        notify();
      } catch (e) {
        if (!navigator.onLine || !e?.response) break; // network/offline — retry later
        await delItem(it.id); // server rejected it (e.g. 400) — drop so the queue can drain
        notify();
      }
    }
  } finally {
    flushing = false;
  }
}

export function useOfflineUploads() {
  const [pending, setPending] = useState(0);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);

  useEffect(() => {
    let alive = true;
    const refresh = () => pendingCount().then((n) => { if (alive) setPending(n); }).catch(() => {});
    refresh();
    const unsub = subscribe(refresh);
    const onOnline = () => { setOnline(true); flush().then(refresh); };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    flush().then(refresh); // attempt on mount in case we reloaded with items queued
    return () => { alive = false; unsub(); window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline); };
  }, []);

  return { pending, online };
}
